import PortalIdParser from 'PortalIdParser';
import { useEffect, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Spacer } from 'foundations-components/components/Spacer';
import { Button } from 'foundations-components/transitional/components/Button';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Pictogram } from 'foundations-components/transitional/components/Pictogram';
import { ACRouterButton } from 'academy-common-lib/components/ACRouterButton';
import { ProfilePageHeader } from './ProfilePageHeader';
import { ContentWrapper } from '../../components/ContentWrapper';
import { Header } from '../../components/Header';
import { AccreditationGridSection } from './AccreditationGridSection';
import { useGetUserProfile } from '../../data/userProfile';
import { ProductUsageSection } from './ProductUsageSection';
import { useGetUserCredentials } from '../../data/accreditation';
import { useIsUserInOwnHub } from '../../data/userSearch';
import useToggle from '../../../../hooks/useToggle';
import ProgressTransferContainer from '../../../certificate/components/progressTransfer/ProgressTransferContainer';
import ApiError from '../../../../components/error/ApiError';
import ErrorBoundary from '../../../../components/error/ErrorBoundary';
import { ProfileCardWrapper } from '../../components/ProfileCardWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function categoriseCredentials(credentials) {
  return credentials.reduce((acc, credential) => {
    switch (credential.type) {
      case 'badge':
        acc.badges.push(credential);
        break;
      case 'certification':
        acc.certifications.push(credential);
        break;
      case 'accreditation':
        acc.accreditations.push(credential);
        break;
      default:
        // somehow we got an unknown credential type
        break;
    }
    return acc;
  }, {
    badges: [],
    certifications: [],
    accreditations: []
  });
}
const CurrentUserEmptyProfileMessage = ({
  editPath
}) => {
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "start",
    flexDirection: "column",
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h2",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "profilePage.AcademyProfile.emptyState.title"
      })
    }), /*#__PURE__*/_jsx(Spacer, {
      space: "400"
    }), /*#__PURE__*/_jsx(Text, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "profilePage.AcademyProfile.emptyState.description"
      })
    }), /*#__PURE__*/_jsx(Spacer, {
      space: "600"
    }), /*#__PURE__*/_jsx(ACRouterButton, {
      to: editPath,
      use: "primary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "profilePage.header.editProfileButton"
      })
    })]
  });
};
const OtherUsersEmptyProfileMessage = ({
  name
}) => {
  const isHublessProfile = !PortalIdParser.get();
  return /*#__PURE__*/_jsx(EmptyState, {
    primaryContent: /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "profilePage.AcademyProfile.emptyState.otherUser.description",
        options: {
          name
        }
      }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "profilePage.AcademyProfile.emptyState.otherUser.ctaDescription"
      }), /*#__PURE__*/_jsx("div", {
        children: isHublessProfile ? /*#__PURE__*/_jsx(Button, {
          href: "/academy/results",
          marginBlockStart: "600",
          size: "small",
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "profilePage.AcademyProfile.emptyState.otherUser.ctaText"
          })
        }) : /*#__PURE__*/_jsx(ACRouterButton, {
          marginBlockStart: "600",
          size: "small",
          to: "/results",
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "profilePage.AcademyProfile.emptyState.otherUser.ctaText"
          })
        })
      })]
    }),
    secondaryContent: /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(Pictogram, {
        blockSize: "180px",
        inlineSize: "100%",
        name: "CelebrationSuccess"
      })
    }),
    secondaryContentWidth: 180,
    titleText: "Achievements"
  });
};
export const ProfilePage = ({
  authedUser,
  trackPageView,
  trackInteraction,
  successMarker,
  failureMarker,
  notFoundMarker,
  profileId,
  showHeader = true,
  editPath,
  isHubless,
  showBadgeShareButton
}) => {
  var _authedUser$id, _credentialsData$cred;
  const profileIdNumber = parseInt(profileId, 10);
  const isAuthedUsersProfile = (authedUser === null || authedUser === void 0 || (_authedUser$id = authedUser.id) === null || _authedUser$id === void 0 ? void 0 : _authedUser$id.toString()) === profileId;
  const {
    isUserInOwnHub
  } = useIsUserInOwnHub(profileIdNumber);
  const {
    isShowing: isTransferModalOpen,
    show: showTransferModal,
    hide: hideTransferModal
  } = useToggle(false);
  const {
    data: userProfile,
    loading,
    error
  } = useGetUserProfile(profileIdNumber);
  const {
    data: credentialsData
  } = useGetUserCredentials(profileIdNumber);
  const userCertificateNames = (credentialsData === null || credentialsData === void 0 || (_credentialsData$cred = credentialsData.credentials) === null || _credentialsData$cred === void 0 ? void 0 : _credentialsData$cred.map(credential => credential.title)) || [];
  useEffect(() => {
    if (userProfile) {
      trackPageView(profileIdNumber);
    }
  }, [profileIdNumber, trackPageView, userProfile]);
  const categorisedCredentials = useMemo(() => categoriseCredentials((credentialsData === null || credentialsData === void 0 ? void 0 : credentialsData.credentials) || []), [credentialsData]);
  if (error && error.networkError) {
    const castedNetworkError = error.networkError;
    if (castedNetworkError.status === 404) {
      return /*#__PURE__*/_jsxs(View, {
        children: [notFoundMarker, showHeader && /*#__PURE__*/_jsx(Header, {}), /*#__PURE__*/_jsx(ErrorMessage, {
          type: "notFound",
          children: /*#__PURE__*/_jsx(Text, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "profilePage.AcademyProfile.errors.notFound.message"
            })
          })
        })]
      });
    }
    return /*#__PURE__*/_jsxs(View, {
      children: [showHeader && /*#__PURE__*/_jsx(Header, {}), failureMarker, /*#__PURE__*/_jsx(ErrorMessage, {
        type: "badRequest"
      })]
    });
  }
  if (loading || !userProfile) {
    return /*#__PURE__*/_jsxs(View, {
      children: [showHeader && /*#__PURE__*/_jsx(Header, {}), /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true,
        layout: "centered",
        marginBlockStart: "400"
      })]
    });
  }
  const hasCertifications = categorisedCredentials.certifications.length > 0;
  const hasBadges = categorisedCredentials.badges.length > 0;
  const hasAccreditations = categorisedCredentials.accreditations.length > 0;
  const showCurrentUserEmptyProfileMessage = isAuthedUsersProfile && !userProfile.user_profile.profileExists && !hasCertifications && !hasBadges && !hasAccreditations;
  const showOtherUsersEmptyProfileMessage = !isAuthedUsersProfile && !hasCertifications && !hasBadges && !hasAccreditations && !userProfile.user_profile.products.length;
  const isEmptyProfile = showCurrentUserEmptyProfileMessage || showOtherUsersEmptyProfileMessage;
  const profileContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [userProfile.user_profile.biography && /*#__PURE__*/_jsxs(View, {
      children: [/*#__PURE__*/_jsxs(ProfileCardWrapper, {
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h4",
          marginBlockEnd: "400",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "profilePage.AcademyProfile.biography.title"
          })
        }), /*#__PURE__*/_jsx(Text, {
          children: userProfile.user_profile.biography
        })]
      }), /*#__PURE__*/_jsx(Spacer, {
        space: "400"
      })]
    }), /*#__PURE__*/_jsxs(View, {
      children: [hasCertifications && /*#__PURE__*/_jsx(View, {
        marginBlockEnd: "400",
        children: /*#__PURE__*/_jsx(ProfileCardWrapper, {
          children: /*#__PURE__*/_jsx(ErrorBoundary, {
            boundaryName: "CertificationsGrid",
            children: /*#__PURE__*/_jsx(AccreditationGridSection, {
              accreditations: categorisedCredentials.certifications,
              onTransferAwardsClick: showTransferModal,
              showCertificateModals: isUserInOwnHub,
              showTransferButton: isAuthedUsersProfile && isUserInOwnHub,
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "profilePage.AcademyProfile.accreditations.types.certifications"
              }),
              transferButtonLabel: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "profilePage.AcademyProfile.accreditations.transferCertificatesButton"
              })
            })
          })
        })
      }), hasBadges && /*#__PURE__*/_jsxs(View, {
        children: [/*#__PURE__*/_jsx(ProfileCardWrapper, {
          children: /*#__PURE__*/_jsx(ErrorBoundary, {
            boundaryName: "BadgesGrid",
            children: /*#__PURE__*/_jsx(AccreditationGridSection, {
              accreditations: categorisedCredentials.badges,
              onTransferAwardsClick: showTransferModal,
              showBadgeShareButton: showBadgeShareButton,
              showCertificateModals: isUserInOwnHub,
              showTransferButton: isAuthedUsersProfile && isUserInOwnHub,
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "profilePage.AcademyProfile.accreditations.types.badges"
              }),
              transferButtonLabel: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "profilePage.AcademyProfile.accreditations.transferBadgesButton"
              })
            })
          })
        }), /*#__PURE__*/_jsx(Spacer, {
          space: "400"
        })]
      }), hasAccreditations && /*#__PURE__*/_jsxs(View, {
        children: [/*#__PURE__*/_jsx(ProfileCardWrapper, {
          children: /*#__PURE__*/_jsx(ErrorBoundary, {
            boundaryName: "AccreditationsGrid",
            children: /*#__PURE__*/_jsx(AccreditationGridSection, {
              accreditations: categorisedCredentials.accreditations,
              title: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "profilePage.AcademyProfile.accreditations.types.accreditations"
              })
            })
          })
        }), /*#__PURE__*/_jsx(Spacer, {
          space: "800"
        })]
      }), userProfile.user_profile.products.length > 0 && /*#__PURE__*/_jsx(View, {
        children: /*#__PURE__*/_jsx(ProfileCardWrapper, {
          children: /*#__PURE__*/_jsx(ErrorBoundary, {
            boundaryName: "ProductUsageSection",
            children: /*#__PURE__*/_jsx(ProductUsageSection, {
              products: userProfile.user_profile.products
            })
          })
        })
      })]
    })]
  });
  return /*#__PURE__*/_jsxs(View, {
    backgroundColor: "fill-surface-recessed",
    paddingBlockEnd: "1000",
    children: [successMarker, showHeader && /*#__PURE__*/_jsx(Header, {}), /*#__PURE__*/_jsx(ErrorBoundary, {
      boundaryName: "ProfilePageHeader",
      children: /*#__PURE__*/_jsx(ProfilePageHeader, {
        editPath: editPath,
        hasEditAccess: isAuthedUsersProfile,
        isHubless: isHubless,
        trackInteraction: trackInteraction,
        userProfile: userProfile.user_profile
      })
    }), /*#__PURE__*/_jsx(ContentWrapper, {
      isHubless: isHubless,
      children: /*#__PURE__*/_jsxs(View, {
        marginBlock: "400",
        children: [showCurrentUserEmptyProfileMessage && /*#__PURE__*/_jsx(ProfileCardWrapper, {
          children: /*#__PURE__*/_jsx(CurrentUserEmptyProfileMessage, {
            editPath: editPath
          })
        }), showOtherUsersEmptyProfileMessage && /*#__PURE__*/_jsx(ProfileCardWrapper, {
          children: /*#__PURE__*/_jsx(OtherUsersEmptyProfileMessage, {
            name: userProfile.user_profile.displayName
          })
        }), !isEmptyProfile && profileContent]
      })
    }), isTransferModalOpen && /*#__PURE__*/_jsx(ProgressTransferContainer, {
      ErrorComponent: ApiError,
      currentUserEmail: (authedUser === null || authedUser === void 0 ? void 0 : authedUser.email) || '',
      handleClose: hideTransferModal,
      isOpen: isTransferModalOpen,
      trackEvent: eventProps => {
        if (eventProps) {
          trackInteraction(eventProps === null || eventProps === void 0 ? void 0 : eventProps.action, eventProps);
        }
      },
      userCredentials: userCertificateNames
    })]
  });
};